import React, { useRef, useState } from 'react';
import { Content, Item, Portal, Root, Trigger } from '@radix-ui/react-dropdown-menu';
import type { CSS } from '@components-shared/src/stitches';
import { keyframes, styled } from '@components-shared/src/stitches';

export type DropdownAlign = 'start' | 'center' | 'end';

//TODO: Refactor to follow compound pattern https://linear.app/sound/issue/ENG-4446/refactor-global-dropdown-to-follow-compound-pattern
export const GlobalDropdown = ({
  trigger,
  children,
  align,
  css,
  onOpenChange,
  queue,
  wrapperContentProps,
  isModal = false,
}: {
  trigger: React.ReactNode;
  children?: React.ReactNode;
  align?: DropdownAlign;
  css?: CSS;
  onOpenChange?: () => void;
  queue?: boolean;
  wrapperContentProps?: Partial<React.ComponentProps<typeof DropdownMenuContent>>;
  isModal?: boolean;
}) => {
  const isScrolling = useRef(false);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box
      css={css}
      onPointerDownCapture={event => {
        // prevent opening dropdown when scrolling on touch devices
        if (event.pointerType === 'touch' && !isOpen) event.preventDefault();
      }}
      onTouchMove={() => {
        isScrolling.current = true;
      }}
      onTouchEnd={() => {
        if (!isScrolling.current && !isOpen) setIsOpen(true);
        isScrolling.current = false;
      }}
      onClick={e => {
        //prevent events on the parent element of the dropdown to get executed instead of the dropdown elements events
        e.stopPropagation();
      }}
    >
      <DropdownMenu
        open={isOpen}
        modal={isModal}
        onOpenChange={open => {
          setIsOpen(open);
          onOpenChange?.();
        }}
      >
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>

        <DropdownMenuContent align={align} queue={queue} {...wrapperContentProps}>
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    </Box>
  );
};

const Box = styled('div', {
  width: 'auto',
});

export const MenuItemLabel = styled('span', {
  marginLeft: 8,
});

const slideUpAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideRightAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(-2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const slideDownAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(-2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

const slideLeftAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const StyledContent = styled(Content, {
  zIndex: '$dropdown',
  minWidth: 172,
  '@tablet': {
    minWidth: 190,
  },
  marginTop: 4,
  backgroundColor: 'white',
  borderRadius: 6,
  border: '1.5px solid $neutral200',
  boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 6px 16px rgba(0, 0, 0, 0.12);',
  '@media (prefers-reduced-motion: no-preference)': {
    animationDuration: '400ms',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    willChange: 'transform, opacity',
    '&[data-state="open"]': {
      '&[data-side="top"]': { animationName: slideDownAndFade },
      '&[data-side="right"]': { animationName: slideLeftAndFade },
      '&[data-side="bottom"]': { animationName: slideUpAndFade },
      '&[data-side="left"]': { animationName: slideRightAndFade },
    },
  },
  variants: {
    queue: {
      true: {
        zIndex: '$audioPlayerQueueDropdown',
      },
    },
    compactWidth: {
      true: {
        minWidth: 'unset',
      },
    },
  },
});

function WrappedContent({
  children,
  disablePortal = false,
  ...props
}: {
  children: React.ReactNode;
  disablePortal?: boolean;
} & React.ComponentProps<typeof StyledContent>) {
  if (disablePortal) return <StyledContent {...props}>{children}</StyledContent>;
  return (
    <Portal>
      <StyledContent {...props}>{children}</StyledContent>
    </Portal>
  );
}

const StyledItem = styled(Item, {
  all: 'unset',
  base: 'M',
  lineHeight: '14px',
  fontStyle: 'medium',
  display: 'flex',
  padding: '6px 10px',
  alignItems: 'center',
  color: '$neutral800',
  height: 32,
  borderRadius: 6,
  position: 'relative',
  userSelect: 'none',
  cursor: 'pointer',
  '&[data-disabled]': {
    color: '$neutral400',
    pointerEvents: 'none',
  },
  '&[data-highlighted]': {
    backgroundColor: '$neutral100',
  },
  svg: {
    width: 20,
  },
});

export const DropdownMenu = Root;
export const DropdownMenuTrigger = Trigger;
export const DropdownMenuContent = WrappedContent;
export const DropdownMenuItem = StyledItem;
